import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../styles/PendingTicket.css";
import { ArrowRight } from "lucide-react"; 

//import lottoLogo from "../Images/NATIONAL.png";
import api from "../context/api";

// Import all logos
import nationalLogo from "../Images/NATIONAL.png";
import mondayLogo from "../Images/MONDAY.png";
import tuesdayLogo from "../Images/TUESDAY.png";
import midweekLogo from "../Images/MIDWEEK.png";
import thursdayLogo from "../Images/THURSDAY.png";
import fridayLogo from "../Images/FRIDAY.png";
import sundayLogo from "../Images/SUNDAY.png";
import huntexLogo from "../Images/Huntex.png";

const isHuntexGameName = (gameName = "") => /^huntex/i.test(String(gameName).trim());

// Map lottery types to their logos
export const logoMap = {
  "NATIONAL WEEKLY": nationalLogo,
  "MONDAY SPECIAL": mondayLogo,
  "LUCKY TUESDAY": tuesdayLogo,
  "MIDWEEK": midweekLogo,
  "FORTUNE THURSDAY": thursdayLogo,
  "FRIDAY BONANZA": fridayLogo,
  "SUNDAY ASEDA": sundayLogo,
  "HUNTEX LOTTO": huntexLogo,
};


const PendingTicket = ({ ticket, setPendingTicket, confirmInfo }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(AuthContext);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null });
  const [isProcessing, setIsProcessing] = useState(false);

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 3000);
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  // Helper: determine if a ticket has expired based on draw date + closing time
  const isTicketExpired = (t) => {
    try {
      if (!t?.drawDate || !t?.closingTime) return false;

      // First try to parse full drawDate + closingTime (which may include seconds/AM-PM)
      let closingDateTime = new Date(`${t.drawDate} ${t.closingTime}`);

      // If that fails, fall back to simple HH:MM parsing
      if (isNaN(closingDateTime.getTime())) {
        const baseDate = new Date(t.drawDate);
        if (isNaN(baseDate.getTime())) return false;

        const timeParts = String(t.closingTime).split(":");
        if (timeParts.length < 2) return false;

        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        if (isNaN(hours) || isNaN(minutes)) return false;

        closingDateTime = new Date(baseDate.getTime());
        closingDateTime.setHours(hours, minutes, 0, 0);
      }

      const now = new Date();
      return now > closingDateTime;
    } catch (e) {
      // If anything goes wrong, treat as not expired to avoid false removals
      return false;
    }
  };

  // Filter out expired tickets for display
  const visibleTickets = Array.isArray(ticket)
    ? ticket.filter((t) => !isTicketExpired(t))
    : [];

  // Automatically prune expired tickets from pending list so they are removed from storage/state too
  useEffect(() => {
    if (!Array.isArray(ticket) || ticket.length === 0) return;

    if (visibleTickets.length !== ticket.length) {
      setPendingTicket(visibleTickets);
    }
  }, [ticket, visibleTickets, setPendingTicket]);

  if (!visibleTickets.length) return null;

  // Actual confirm logic that talks to the API
  const performConfirm = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    // First, drop any tickets whose draw date + closing time have expired
    // and any that don't have valid time info
    const activeTickets = visibleTickets.filter(
      (t) => t.drawDate && t.closingTime
    );

    if (activeTickets.length === 0) {
      showToast("All pending games have expired and have been removed.", "error");
      setPendingTicket([]);
      return;
    }

    if (activeTickets.length !== ticket.length) {
      showToast(
        "Some pending games were expired or missing time information and were removed. Only valid active games will be played.",
        "info"
      );
      setPendingTicket(activeTickets);
    }

    const overCapTicket = activeTickets.find((t) => {
      if (!isHuntexGameName(t?.lotteryType)) return false;
      const ticketCap = Number(t?.winCap);
      if (!Number.isFinite(ticketCap) || ticketCap <= 0) return false;
      const minimumWin = Number(t?.minimumWin);
      return Number.isFinite(minimumWin) && minimumWin > ticketCap;
    });

    if (overCapTicket) {
      const ticketCap = Number(overCapTicket?.winCap);
      const cap = Number.isFinite(ticketCap) && ticketCap > 0 ? ticketCap : 0;
      const minimumWin = Number(overCapTicket?.minimumWin || 0);
      showToast(
        `${overCapTicket.lotteryType}: minimum win (${minimumWin.toLocaleString()}) exceeds cap (${cap.toLocaleString()}). Please reduce stake.`,
        "error"
      );
      return;
    }

    // Prepare the payload for only active (non-expired) tickets
    const payload = activeTickets.map((t) => ({
      id: t.id,
      selectedNumbers: t.numbers,
      againstNumbers: t.againstNumbers,
      amount: Number(t.amount ?? 0),
      price: t.price,
      minimumWin: t.minimumWin,
      maximumWin: t.maximumWin,
      selectedDay: t.lotteryType,
      NextGameDate: t.drawDate,
      limit: t.limit,
      lines: t.lines,
      closingTime: t.closingTime,
    }));

    try {
      // Save only active tickets to database (adjust endpoint as needed)
      for (const t of payload) {
        await api.post("/saveNumbers", t);
      }
      showToast("Tickets confirmed and played!", "success");
      setPendingTicket([]);
      // Optionally navigate to receipt or success page
      //navigate("/UserReceipt", { state: { ...payload.data } });
    } catch (err) {
      console.error("Error confirming pending tickets:", err?.response || err);
      const message =
        err?.response?.data?.message || "Network error. Please try again.";
      showToast(message, "error");
    }
  };

  // Handler for the CONFIRM button – shows the same style of
  // confirmation dialog as in PlayGame1 before running performConfirm.
  const handleConfirm = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    const totalStake = visibleTickets.reduce(
      (sum, t) => sum + Number( t.price),
      0
    );

    const message = `You are about to confirm and play ${visibleTickets.length} pending ticket(s).\nTotal stake: GHC ${totalStake.toFixed(
      2
    )}.\n\nDo you want to proceed?`;

    showConfirm(message, () => performConfirm());
  };



  // Handler to delete a ticket by index
  const handleDelete = (idx) => {
    setPendingTicket((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="pending-tickets">
      <h2>Pending Tickets</h2>
      <table className="ticket-table">
        <thead>
          <tr>
            <th>Lottery Type</th>
            <th>Type</th>
            <th>Your Numbers</th>
            <th>Lines</th>
            <th>Price (GHC)</th>
            <th>Win</th>
            <th>Draw Date</th>
            <th>Closing Time</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
            {visibleTickets.map((t, idx) => (
            <tr key={idx}>
              <td className="lotto-name">
                <img
                  src={logoMap[t.lotteryType] || nationalLogo}
                  alt={`${t.lotteryType} Logo`}
                />              
                  {t.lotteryType}
              </td>
              <td>{t.type}</td>
              <td>
                {t.numbers.map((num, i) => (
                  <span key={i} className="num1">
                    {num}
                  </span>
                ))}
              </td>
              <td>{t.lines}</td>
              <td>
                {Number(t.price).toFixed(2)} <br/>
                <small>stake: {Number(t.amount || 0).toFixed(2)}</small>
              </td>
              <td>{Number(t.win || 0).toFixed(2)}</td>
              <td>{t.drawDate}</td>
              <td>{t.closingTime}</td>
              <td>
                <button onClick={() => handleDelete(idx)}>🗑</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ticket-summary">
        <span>
          <strong>Total Price:</strong> GHC{" "}
          {visibleTickets.reduce((sum, t) => sum + Number(t.price), 0).toFixed(2)}
        </span>
        <span>
          <strong>Total Win:</strong> GHC{" "}
          {visibleTickets.reduce((sum, t) => sum + Number(t.win || 0), 0).toLocaleString()}
        </span>
      </div>

      <div className="ticket-confirm">
        <button className="confirm-btn" onClick={handleConfirm}>CONFIRM <ArrowRight size={20} style={{ verticalAlign: "middle" }} /> </button>
      </div>

      {confirmDialog.show && (
        <div
          className="confirm-overlay"
          onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Pending Tickets</h3>
            <div className="confirm-message">
              {confirmDialog.message.split("\n").map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div className="confirm-buttons">
              <button
                className="confirm-btn cancel-btn"
                onClick={() =>
                  setConfirmDialog({ show: false, message: "", onConfirm: null })
                }
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="confirm-btn proceed-btn"
                onClick={async () => {
                  setIsProcessing(true);
                  setConfirmDialog({ show: false, message: "", onConfirm: null });
                  if (confirmDialog.onConfirm) {
                    await confirmDialog.onConfirm();
                  }
                  setIsProcessing(false);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default PendingTicket;
